/**
 * 集成到现有的 AutoTrigger
 * 在现有 AutoTrigger 的基础上，增加新架构的处理
 *
 * ⚠️ 重要：不修改旧代码，只是在旧代码之后增加新架构的处理
 */

import { autoTriggerAdapter } from "../adapter/AutoTriggerAdapter";
import { shouldUseV2 } from "../config/migration";

/**
 * 集成到现有的 AutoTrigger
 *
 * 使用方式：
 * 1. 在 tracker/autoTrigger.js 中引入此函数
 * 2. 在初始化时调用 integrateWithAutoTrigger(trigger)
 *
 * @param autoTrigger 现有的 autoTrigger 实例
 */
export function integrateWithAutoTrigger(autoTrigger: any): void {
  if (!autoTrigger || typeof autoTrigger.on !== "function") {
    console.error("[Integration] Invalid autoTrigger instance");
    return;
  }

  // 保存原有的 on 方法
  const originalOn = autoTrigger.on.bind(autoTrigger);

  // 重写 on 方法
  autoTrigger.on = function (eventName: string, handler: Function) {
    // 1. 原有的处理逻辑（完全不动）
    originalOn(eventName, handler);

    // 2. 新增：如果配置开启新架构，也使用新架构处理
    originalOn(eventName, (targetNode: Element) => {
      try {
        // 获取页面名称
        const pageName = getPageName();

        // 检查是否应该使用新架构
        const { useV2 } = shouldUseV2(eventName, pageName);

        if (useV2) {
          // 使用新架构上报到新表
          autoTriggerAdapter.handleAutoTrigger(eventName, targetNode);
        }

        // ⚠️ 注意：旧埋点 handler 会继续执行，上报到 tracker_v1 表（完全不动）
        // 结果是：同一事件上报两份数据到两个表，用于数据对比验证
      } catch (error) {
        console.error("[Integration] Error handling V2 event:", error);
        // 错误不影响旧埋点的执行
      }
    });
  };

  if (process.env.NODE_ENV === "development") {
    console.log("[Integration] Successfully integrated with AutoTrigger");
  }
}

/**
 * 获取页面名称（辅助函数）
 */
function getPageName(): string {
  try {
    if (typeof window !== "undefined" && (window as any).getPage) {
      const page = (window as any).getPage();
      return page?.page_nm || "";
    }
  } catch (e) {
    // 降级处理
  }
  return "";
}

/**
 * 初始化新架构
 * 在应用启动时调用，集成到现有 tracker
 */
export function initTrackerV2(): void {
  try {
    // 等待现有 tracker 初始化
    if (typeof window !== "undefined") {
      const checkAndIntegrate = () => {
        const trigger = (window as any).trigger || (window as any).autoTrigger;

        if (trigger) {
          integrateWithAutoTrigger(trigger);

          if (process.env.NODE_ENV === "development") {
            console.log("[TrackerV2] Initialization complete");
          }
        } else {
          // 如果还没有初始化，延迟重试
          setTimeout(checkAndIntegrate, 100);
        }
      };

      checkAndIntegrate();
    }
  } catch (error) {
    console.error("[TrackerV2] Initialization error:", error);
  }
}
